import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ViewBody } from '@ayumia/contracts';
import { collectionInputs, evaluateViewCondition, isCoreRepresentation } from '@ayumia/component-runtime';
import type { ListInput } from '../api/client';
import { text } from '../app/i18n';
import { Icon } from '../design-system/icons';
import { Btn, ListToolbar, QuickFilterChips, QuickFilterTabs, SelectMenu, TableFooterTotals } from '../design-system/primitives';
import { Empty, errorView, ListSkeleton } from '../states/States';
import { cardZonesOf, collectionRepresentations, containerFeatures, listColumns, representationLabel, type Representation } from './assembly';
import { AgendaRenderer } from './collection/AgendaRenderer';
import { WeekGridRenderer } from './collection/WeekGridRenderer';
import { BulkBar, type BulkAction } from './collection/BulkBar';
import { bulkActionSpec } from './collection/bulk-actions';
import { displayPreferenceKey, readDisplayPreference, writeDisplayPreference } from './collection/display-preference';
import { CardRenderer } from './collection/CardRenderer';
import { zoneFields } from './collection/CardZones';
import { GroupedCollection } from './collection/GroupedCollection';
import { RowActionsContext } from './collection/RowActionCell';
import { ENVELOPE_SORTABLE, useCellRenderer, type Column } from './collection/cells';
import { BackToTop, InfiniteSentinel } from './collection/InfiniteSentinel';
import { KanbanRenderer } from './collection/KanbanRenderer';
import { selectionKeys, useSelection } from './collection/selection';
import { TableRenderer } from './collection/TableRenderer';
import { useCollection, type Row } from './collection/useCollection';
import { FilterBar, type Filters } from './FilterBar';
import { fieldLabel, fieldOf, type ObjectSummary } from './format';
import { ListExportDialog, type ListExport } from './ListExportDialog';
import { AGGREGATION_LABELS, formatTotal } from './collection/totals';
import { activePeriod, activeQuickFilter, civil, formatListState, isNarrowed, mergeFilters, parseListState, periodFilter, periodRange, PRESET_LABELS, type ListState, type PeriodPreset, type SortState } from './list-state';
import { useLoad } from './useLoad';
import { useAppServices, type ViewCommand } from './ViewContext';
import './collection/collection.css';

/**
 * Renderer du composant Core `Collection` (59 §5.1, §9.4 ; ADR-094, ADR-096, ADR-099) : il possède
 * la requête (recherche, filtres rapides, période, filtres par champ, tri), son état dans l'URL, le
 * chargement par défilement infini, les totaux, l'export, la sélection et les actions de masse ; il
 * rend l'instance de **représentation** choisie — `Table`, `CardList`, `Kanban`, `Agenda` — qui
 * ne fait qu'afficher ses entrées. Changer de représentation change l'affichage, jamais la requête
 * ni la sélection. Les contrôles montrés sont ceux que le manifeste du composant actif déclare
 * (`container_features`). La source est celle de la vue (`data_source`) ; le serveur revalide
 * tout ; une erreur n'est jamais une liste vide.
 */

const SEARCH_DEBOUNCE_MS = 400;
const DEFAULT_PAGE_SIZE = 50;

function typing(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

export function ListRepresentation({
  view,
  object,
  onOpen,
  selectedId,
  refreshKey,
  fixedFilter,
  listExport,
  state: serialized,
  onStateChange,
}: {
  view: ViewBody;
  object: ObjectSummary | undefined;
  onOpen: (row: Row) => void;
  selectedId: string | null;
  refreshKey: number;
  /** Filtre imposé par le contexte (vue embarquée sous un enregistrement) ; l'utilisateur ne le retire pas. */
  fixedFilter?: Filters;
  /** Export présentable résolu par `GetView` (permission accordée) ; absent : pas de bouton. */
  listExport?: ListExport;
  /** État sérialisé dans l'URL ; absent (vue embarquée), l'état reste local. */
  state?: string;
  onStateChange?: (next: string, options: { replace: boolean }) => void;
}) {
  const services = useAppServices();
  const list = collectionInputs(view);
  const type = view.target_object_type!;
  const pageSize = view.data_source.page_size ?? DEFAULT_PAGE_SIZE;

  // État : l'URL si la page la fournit, sinon un état local (vue embarquée).
  const [local, setLocal] = useState('');
  const current = onStateChange === undefined ? local : (serialized ?? '');
  const state = useMemo(() => parseListState(current), [current]);
  const write = (next: ListState, options: { replace: boolean } = { replace: false }): void => {
    const serializedNext = formatListState(next);
    if (onStateChange === undefined) setLocal(serializedNext);
    else onStateChange(serializedNext, options);
  };
  const update = (patch: Partial<ListState>, options: { replace: boolean } = { replace: false }): void => write({ ...state, ...patch }, options);

  // Représentation : celle de l'URL si la vue l'offre, sinon le choix gardé pour la personne (ADR-149
  // §1, préférence locale), sinon l'entrée de la vue, sinon la première.
  const representations = collectionRepresentations(view);
  const preferenceKey = list.display?.remember === false || representations.length < 2 ? null : displayPreferenceKey(`list.${type}.${representations.map((entry) => entry.id).join('-')}`, services.context);
  const [remembered] = useState(() => (preferenceKey === null ? undefined : readDisplayPreference(preferenceKey, representations.map((entry) => entry.id))));
  const active: Representation | undefined = representations.find((entry) => entry.id === state.view) ?? representations.find((entry) => entry.id === remembered) ?? representations.find((entry) => entry.id === view.entry) ?? representations[0];
  const listLike = active !== undefined && (isCoreRepresentation(active, 'Table') || isCoreRepresentation(active, 'CardList'));
  // Contrôles du conteneur que le composant actif prend en charge (container_features de son manifeste).
  const features = containerFeatures(active);
  const controls = { sort: features.has('sort'), period: features.has('period'), totals: features.has('totals'), selection: features.has('selection'), selectAll: features.has('select_all'), search: features.has('search'), quickFilters: features.has('quick_filters'), filters: features.has('filters'), export: features.has('export') };

  // Recherche : brouillon local, appliqué après 400 ms de calme, sur Entrée, ou à l'effacement.
  const [draft, setDraft] = useState(state.q);
  useEffect(() => setDraft(state.q), [state.q]);
  const commitSearch = (value: string): void => {
    if (value.trim() !== state.q) update({ q: value.trim() }, { replace: true });
  };
  useEffect(() => {
    if (draft.trim() === state.q) return;
    const timer = setTimeout(() => commitSearch(draft), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  });
  const searchInput = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (list.search === undefined) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey || typing(event.target)) return;
      event.preventDefault();
      searchInput.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [list.search]);

  const quick = activeQuickFilter(list, state);
  const preset = activePeriod(list, state);
  const periodType = list.period === undefined ? undefined : list.period.field === 'created_at' || list.period.field === 'updated_at' ? 'datetime' : fieldOf(object, list.period.field)?.type;
  const sort: SortState | null = state.sort ?? view.data_source.default_sort?.[0] ?? null;
  const baseFilter = mergeFilters(periodFilter(list, preset, periodType), state.filters, fixedFilter);
  const effective = mergeFilters(quick?.filter, baseFilter);
  const q = state.q === '' ? undefined : state.q;
  const searchFields = list.search?.fields;
  const search = { ...(q === undefined ? {} : { q }), ...(q === undefined || searchFields === undefined ? {} : { q_fields: searchFields }) };

  const [tick, setTick] = useState(0);
  const refresh = (): void => setTick((value) => value + 1);
  const loadKey = refreshKey * 100000 + tick;
  const listInput: Omit<ListInput, 'cursor' | 'limit' | 'include_total'> = { filter: effective, ...search, ...(sort === null ? {} : { sort: [sort] }), ...(list.totals === undefined ? {} : { totals: list.totals.items }) };
  // Regroupement (ADR-099) : la lecture « groupes » remplace la liste à plat ; la collection ne sert plus que le compteur et les totaux.
  const grouping = active !== undefined && (isCoreRepresentation(active, 'Table') || isCoreRepresentation(active, 'CardList')) ? active.inputs : undefined;
  const groupFields: readonly string[] = grouping?.group_by === undefined ? [] : typeof grouping.group_by === 'string' ? [grouping.group_by] : grouping.group_by;
  const collection = useCollection(listLike ? { type, input: listInput, pageSize: groupFields.length > 0 ? 1 : pageSize, dropSortOnError: true } : null, loadKey);
  const rows = collection.rows;

  // Sélection : partagée par les représentations, vidée quand la requête change (ADR-096 §4).
  // Actions groupées déclarées (ADR-149 §3) : un nom ou un objet à bornes ; seules celles que GetView sert.
  const bulkActions: BulkAction[] = (list.bulk_commands ?? []).map(bulkActionSpec).flatMap((spec) => {
    const command: ViewCommand | undefined = services.commands?.get(spec.command_name);
    return command === undefined ? [] : [{ command, spec }];
  });
  const rowIds = rows.map((row) => row.id).filter((id): id is string => typeof id === 'string');
  const selection = useSelection(collection.total, rowIds, { range: list.selection?.range !== false });
  const queryKey = JSON.stringify({ effective, q, refreshKey });
  // La sélection ne survit pas à un changement de requête : elle désignerait d'autres lignes.
  const clearSelection = selection.clear;
  useEffect(() => clearSelection(), [queryKey, clearSelection]);
  // Cases à cocher (ADR-099) : l'entrée selectable de l'instance ; absente, affichées s'il y a des actions de masse.
  const declaredSelectable = active !== undefined && (isCoreRepresentation(active, 'Table') || isCoreRepresentation(active, 'CardList') || isCoreRepresentation(active, 'Kanban')) ? active.inputs.selectable : undefined;
  const selectable = controls.selection && (declaredSelectable ?? bulkActions.length > 0) ? selection : undefined;
  const onSelectionKey = selectable === undefined || list.selection?.keyboard === false ? undefined : selectionKeys(selectable, () => rowIds);

  // Compteurs d'onglets : même recherche, même période, mêmes filtres, autre filtre rapide.
  const quickItems = list.quick_filters?.items ?? [];
  const countsKey = list.quick_filters?.show_counts === true ? JSON.stringify({ type, baseFilter, q, refreshKey, tick }) : 'none';
  const [counts] = useLoad<Record<string, number | undefined>>(countsKey, async () => {
    if (countsKey === 'none') return {};
    const results = await Promise.allSettled(quickItems.map((item) => services.client.listObjects<Row>(type, { filter: mergeFilters(item.filter, baseFilter), limit: 1, include_total: true, ...search })));
    return Object.fromEntries(quickItems.map((item, index) => {
      const result = results[index]!;
      return [item.quick_filter_id, result.status === 'fulfilled' ? result.value.page?.total : undefined];
    }));
  });

  // Colonnes de la Table de la représentation active, sinon celles de la première Table de la vue (tri, cellules).
  const declaredColumns = active !== undefined && isCoreRepresentation(active, 'Table') ? active.inputs.columns : listColumns(view);
  const columns: Column[] = declaredColumns.filter((column) => evaluateViewCondition(column.visible_when, { record: null, context: services.context }));
  const cards = active !== undefined && isCoreRepresentation(active, 'CardList') ? active.inputs : undefined;
  // Champs que les zones de la carte affichent (ADR-100) : leurs libellés de référence sont lus avec la page.
  const cardFields = zoneFields(cardZonesOf(cards?.card)?.zones);
  const render = useCellRenderer(object, columns, cardFields, rows, collection.meta);

  const [showFilters, setShowFilters] = useState(Object.keys(state.filters).length > 0);
  const [exporting, setExporting] = useState(false);
  const narrowed = isNarrowed(list, state);
  const clearFilters = (): void => {
    setDraft('');
    // Effacer garde le tri et la représentation ; filtre rapide et période reviennent à leur défaut.
    write({ q: '', filters: {}, ...(state.sort === undefined ? {} : { sort: state.sort }), ...(state.view === undefined ? {} : { view: state.view }) });
  };

  // Tri : champs triables des colonnes (filtrables au modèle ou dates d'enveloppe).
  const sortable = (fieldId: string): boolean => ENVELOPE_SORTABLE.has(fieldId) || fieldOf(object, fieldId)?.filterable === true;
  const sortOptions = columns.filter((column) => sortable(column.field)).flatMap((column) => {
    const label = fieldLabel(object, column.field, column.label);
    return [
      { id: `${column.field}:asc`, label: `${label} ↑` },
      { id: `${column.field}:desc`, label: `${label} ↓` },
    ];
  });
  const sortWarning = collection.sortWarning;
  const toggleSort = (field: string): void => update({ sort: sort?.field === field ? { field, direction: sort.direction === 'asc' ? 'desc' : 'asc' } : { field, direction: 'asc' } });

  // Export : paramètres du jeu pré-remplis depuis l'état de la liste ; le reste est dit non appliqué.
  const exportPrefill = (): { initial: Record<string, string>; unmapped: string[] } => {
    const initial: Record<string, string> = {};
    const used = new Set<string>();
    const range = preset === undefined ? null : periodRange(preset);
    for (const [parameter, source] of Object.entries(listExport?.parameter_map ?? {})) {
      if (source === '$period.from' && range !== null) initial[parameter] = civil(range.from);
      else if (source === '$period.to' && range !== null) initial[parameter] = civil(range.to);
      else if (source === '$q' && q !== undefined) {
        initial[parameter] = q;
        used.add('q');
      } else {
        const match = /^\$filter\.([a-z][a-z0-9_]*)\.([a-z_]+)$/.exec(source);
        const value = match === null ? undefined : effective[match[1]!]?.[match[2]!];
        if (match !== null && value !== undefined) {
          initial[parameter] = value;
          used.add(match[1]!);
        }
      }
      if (source.startsWith('$period.') && list.period !== undefined) used.add(list.period.field);
    }
    const unmapped: string[] = [];
    if (q !== undefined && !used.has('q')) unmapped.push(`recherche « ${q} »`);
    for (const field of Object.keys(effective)) if (!used.has(field) && !(fixedFilter !== undefined && field in fixedFilter)) unmapped.push(fieldLabel(object, field));
    return { initial, unmapped };
  };

  const quickOptions = quickItems.map((item) => ({ id: item.quick_filter_id, label: text(item.label) }));
  const countValues = counts.status === 'ready' ? counts.value : undefined;
  const selectQuick = (id: string): void => update({ quick: id });
  const total = collection.total;
  const top = useRef<HTMLDivElement | null>(null);

  let body: ReactNode;
  if (active === undefined) {
    body = <Empty label="Cette vue ne déclare aucune représentation de collection." />;
  } else if (isCoreRepresentation(active, 'Kanban')) {
    body = <KanbanRenderer type={type} object={object} kanban={active.inputs} {...(active.events === undefined ? {} : { events: active.events })} listColumns={columns} baseFilter={mergeFilters(quick?.filter, baseFilter)} search={search} sort={sort} refreshKey={loadKey} {...(selectable === undefined ? {} : { selection: selectable })} selectedId={selectedId} onOpen={onOpen} onChanged={refresh} />;
  } else if (isCoreRepresentation(active, 'Agenda')) {
    body = <AgendaRenderer type={type} object={object} agenda={active.inputs} listColumns={columns} baseFilter={effective} search={search} refreshKey={loadKey} selectedId={selectedId} onOpen={onOpen} />;
  } else if (isCoreRepresentation(active, 'WeekGrid')) {
    body = <WeekGridRenderer type={type} object={object} grid={active.inputs} listColumns={columns} baseFilter={effective} search={search} refreshKey={loadKey} selectedId={selectedId} onOpen={onOpen} />;
  } else if (!listLike) {
    body = <Empty label={`Le composant « ${active.component} » n'a pas encore de renderer de collection.`} />;
  } else if (collection.status === 'error' && rows.length === 0) {
    // Une erreur n'est jamais présentée comme une liste vide : message et « Réessayer ».
    body = (
      <div className="ay-table-wrap">
        {errorView(collection.error!, collection.reload)}
        {narrowed && (
          <div className="ay-state__actions">
            <button type="button" className="ay-button ay-button--secondary" onClick={clearFilters}>
              Effacer les filtres
            </button>
          </div>
        )}
      </div>
    );
  } else if (collection.status === 'loading' && rows.length === 0) {
    body = (
      <div className="ay-table-wrap">
        <ListSkeleton />
      </div>
    );
  } else if (rows.length === 0) {
    body = (
      <div className="ay-table-wrap" aria-busy={collection.status === 'loading'}>
        {narrowed ? (
          <Empty title="Aucun résultat" label="Aucun élément ne correspond.">
            <button type="button" className="ay-button ay-button--secondary" onClick={clearFilters}>
              Effacer les filtres
            </button>
          </Empty>
        ) : (
          <Empty title="Liste vide" label="Aucun élément pour l’instant." />
        )}
      </div>
    );
  } else {
    const shared = { object, columns, selectedId, onOpen, ...(selectable === undefined ? {} : { selection: selectable }) };
    const renderRows = (shown: readonly Row[], cellRender: typeof render): ReactNode =>
      cards !== undefined ? (
        <CardRenderer {...shared} rows={shown} render={cellRender} card={cards.card} {...(cards.columns_count === undefined ? {} : { columnsCount: cards.columns_count })} />
      ) : (
        <TableRenderer {...shared} rows={shown} render={cellRender} condensed={active !== undefined && isCoreRepresentation(active, 'Table') && active.inputs.density === 'compact'} sort={sortWarning === null ? sort : null} sortable={sortable} onSort={toggleSort} />
      );
    body = (
      <>
        <div className={`ay-table-wrap${collection.status === 'loading' ? ' ay-table-wrap--busy' : ''}`} aria-busy={collection.status === 'loading'}>
          {groupFields.length > 0 ? (
            <GroupedCollection
              type={type}
              object={object}
              fields={groupFields}
              collapsed={grouping?.group_collapsed === true}
              filter={effective}
              search={search}
              sort={sortWarning === null ? sort : null}
              {...(list.totals === undefined ? {} : { totals: list.totals.items, totalLabels: list.totals.items })}
              pageSize={pageSize}
              refreshKey={loadKey}
              columns={columns}
              extraFields={cardFields}
              renderRows={renderRows}
            />
          ) : (
            <>
              {renderRows(rows, render)}
              <InfiniteSentinel loadingMore={collection.loadingMore} done={collection.done} error={collection.moreError} count={total ?? rows.length} onMore={collection.loadMore} />
            </>
          )}
        </div>
        {controls.totals && list.totals !== undefined && collection.totals !== undefined && (
          <TableFooterTotals
            caption={text(list.totals.caption, 'fr', 'Total général')}
            totals={collection.totals.map((entry) => {
              const declared = list.totals!.items.find((item) => item.field_id === entry.field_id && item.aggregation === entry.aggregation);
              const name = fieldLabel(object, entry.field_id);
              return {
                id: `${entry.aggregation}:${entry.field_id}`,
                label: declared?.label !== undefined ? text(declared.label) : entry.aggregation === 'sum' ? name : `${AGGREGATION_LABELS[entry.aggregation]} · ${name}`,
                value: formatTotal(entry, fieldOf(object, entry.field_id)),
                ...(entry.missing === 0 || entry.aggregation === 'count' ? {} : { hint: `${entry.missing.toLocaleString('fr')} élément${entry.missing > 1 ? 's' : ''} sans valeur, non compté${entry.missing > 1 ? 's' : ''}` }),
              };
            })}
          />
        )}
        <div className="ay-list__footer">
          <span className="ay-list__count" role="status">
            {total === undefined ? `${rows.length.toLocaleString('fr')} affiché${rows.length > 1 ? 's' : ''}` : `${total.toLocaleString('fr')} résultat${total > 1 ? 's' : ''}`}
          </span>
        </div>
        <BackToTop anchor={top} />
      </>
    );
  }

  const activeFieldFilters = Object.keys(state.filters).length;
  return (
    <RowActionsContext.Provider value={{ actions: view.actions ?? [], type, onDone: refresh }}>
    <div className="ay-list" ref={top} {...(onSelectionKey === undefined ? {} : { onKeyDown: onSelectionKey })}>
      {quickItems.length > 0 && controls.quickFilters && (list.quick_filters?.presentation ?? 'tabs') === 'tabs' && <QuickFilterTabs items={quickOptions} {...(quick === undefined ? {} : { selected: quick.quick_filter_id })} {...(countValues === undefined ? {} : { counts: countValues })} onSelect={selectQuick} />}
      {/* La barre de sélection prend la place des outils dès qu'un élément est choisi (ADR-149 §3). */}
      {selectable !== undefined && (
        <BulkBar type={type} selection={selectable} total={controls.selectAll ? total : undefined} loadedCount={rows.length} actions={bulkActions} query={{ filter: effective, ...search }} onFinished={refresh} />
      )}
      {(selectable === undefined || selectable.count === 0) && (
      <ListToolbar
        {...(list.search === undefined || !controls.search
          ? {}
          : {
              search: {
                value: draft,
                onChange: setDraft,
                onSubmit: () => commitSearch(draft),
                onClear: () => {
                  setDraft('');
                  commitSearch('');
                },
                placeholder: list.search.placeholder === undefined ? 'Rechercher…' : `${text(list.search.placeholder)}…`,
                inputRef: searchInput,
              },
            })}
        onRefresh={refresh}
        {...(listExport === undefined || !controls.export ? {} : { onExport: () => setExporting(true) })}
        // Sélecteur de représentation à place fixe : basculer ne déplace ni l'en-tête ni la barre (ADR-096).
        trailing={
          representations.length > 1 ? (
            <div className="ay-toggle-group ay-viewswitch" role="group" aria-label="Affichage">
              {representations.map((entry) => {
                const { label, icon } = representationLabel(entry);
                return (
                  <button
                    key={entry.id}
                    type="button"
                    aria-pressed={active?.id === entry.id}
                    title={label}
                    onClick={() => {
                      if (preferenceKey !== null) writeDisplayPreference(preferenceKey, entry.id);
                      update({ view: entry.id }, { replace: true });
                    }}
                  >
                    <Icon name={icon} size={14} />
                    <span className="ay-viewswitch__label">{label}</span>
                  </button>
                );
              })}
            </div>
          ) : undefined
        }
      >
        {quickItems.length > 0 && controls.quickFilters && list.quick_filters?.presentation === 'chips' && <QuickFilterChips items={quickOptions} {...(quick === undefined ? {} : { selected: quick.quick_filter_id })} {...(countValues === undefined ? {} : { counts: countValues })} onSelect={selectQuick} />}
        {(list.filters ?? []).length > 0 && controls.filters && (
          <Btn variant="ghost" icon="filter" aria-expanded={showFilters} aria-controls="ay-list-filters" onClick={() => setShowFilters(!showFilters)}>
            {activeFieldFilters > 0 ? `Filtres (${activeFieldFilters})` : 'Filtres'}
          </Btn>
        )}
        {list.period !== undefined && controls.period && (
          <SelectMenu
            label="Période"
            icon="calendar"
            options={(list.period.presets.includes('all') ? list.period.presets : (['all', ...list.period.presets] as PeriodPreset[])).map((entry) => ({ id: entry, label: PRESET_LABELS[entry] }))}
            {...(preset === undefined ? {} : { selected: preset })}
            onSelect={(id) => update({ period: id as PeriodPreset })}
          />
        )}
        {sortOptions.length > 0 && controls.sort && (
          <SelectMenu
            label="Trier"
            options={sortOptions}
            {...(sort === null ? {} : { selected: `${sort.field}:${sort.direction}` })}
            onSelect={(id) => {
              const [field, direction] = id.split(':') as [string, 'asc' | 'desc'];
              update({ sort: { field, direction } });
            }}
          />
        )}
      </ListToolbar>
      )}
      {showFilters && (
        <div id="ay-list-filters">
          <FilterBar fields={list.filters ?? []} object={object} value={state.filters} onApply={(filters) => update({ filters })} />
        </div>
      )}
      {sortWarning !== null && listLike && (
        <p className="ay-banner ay-banner--warning" role="status">
          Tri par défaut indisponible : {sortWarning} Liste affichée sans tri.
        </p>
      )}
      {body}
      {exporting && listExport !== undefined && (() => {
        const prefill = exportPrefill();
        return <ListExportDialog exported={listExport} initial={prefill.initial} unmapped={prefill.unmapped} onClose={() => setExporting(false)} />;
      })()}
    </div>
    </RowActionsContext.Provider>
  );
}
